import React, { useState } from 'react';
import { colors } from '../theme';
import { locationLabel } from '../utils/rmaLocation';

// Slide-in case-history sidebar for the RMA dashboard (issue #628).
// Rendered next to the stage action modals at a higher z-index, so the action
// UI stays exactly as-is on the left while the full case record — intake
// control, status timeline, repair progress, communication and activity log —
// is scrollable on the right. All data comes from the rma object the
// dashboard already holds; no extra fetches.

const STATUS_LABELS = {
  pending: 'Pending',
  assigned: 'Assigned',
  received: 'Received',
  awaiting_repair: 'Awaiting Repair',
  repairing: 'Repairing',
  awaiting_shipping: 'Awaiting Shipping',
  shipped: 'Shipped',
  closed_case: 'Closed',
  archived: 'Archived',
  cancelled: 'Cancelled',
  canceled_by_customer: 'Cancelled by customer'
};

const fmtWhen = value => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const statusLabel = key => STATUS_LABELS[key] || key || '';

const panelStyles = {
  panel: {
    position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1290,
    width: 'min(420px, 92vw)', overflowY: 'auto',
    background: 'linear-gradient(180deg, rgba(24,30,28,0.99), rgba(16,20,19,0.99))',
    borderLeft: `1px solid ${colors.panelBorder}`,
    boxShadow: '-12px 0 32px rgba(0,0,0,0.45)',
    padding: '1.1rem 1.2rem 2rem',
    color: '#E6E9E7'
  },
  toggle: {
    position: 'fixed', top: '50%', transform: 'translateY(-50%)', zIndex: 1300,
    background: colors.surfaceRaised, color: colors.primary,
    border: `1px solid ${colors.panelBorder}`, borderRight: 'none',
    borderRadius: '10px 0 0 10px', padding: '0.7rem 0.5rem',
    cursor: 'pointer', fontWeight: 700, fontSize: '0.72rem',
    writingMode: 'vertical-rl', textOrientation: 'mixed', letterSpacing: '0.06em'
  },
  sectionTitle: {
    fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: colors.muted, margin: '1.2rem 0 0.45rem'
  },
  card: {
    background: 'rgba(255,255,255,0.035)', border: `1px solid ${colors.panelBorder}`,
    borderRadius: 10, padding: '0.6rem 0.7rem', marginBottom: '0.45rem', fontSize: '0.78rem'
  },
  meta: { color: colors.muted, fontSize: '0.7rem' },
  chip: ok => ({
    display: 'inline-block', padding: '0.1rem 0.55rem', borderRadius: 999,
    fontSize: '0.68rem', fontWeight: 700,
    background: ok ? 'rgba(70,187,137,0.18)' : 'rgba(236,33,36,0.18)',
    color: ok ? colors.primary : colors.danger
  })
};

function Section({ title, children }) {
  return (
    <div>
      <div style={panelStyles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function IntakeBlock({ rma }) {
  const intake = rma.receiptIntake;
  const receipt = rma.receipt;
  if (!intake && !receipt?.confirmedAt) {
    return <div style={{ ...panelStyles.card, color: colors.muted }}>Intake control not recorded yet.</div>;
  }
  return (
    <div style={panelStyles.card}>
      {intake && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={panelStyles.chip(intake.status !== 'issue')}>
              {intake.status === 'issue' ? 'ISSUE AT INTAKE' : 'INTAKE OK'}
            </span>
            <span style={panelStyles.meta}>
              {intake.recordedBy ? `${intake.recordedBy} · ` : ''}{fmtWhen(intake.recordedAt)}
            </span>
          </div>
          <div style={{ marginTop: 6, ...panelStyles.meta }}>
            {intake.shipmentType ? `Shipment: ${intake.shipmentType}` : ''}
            {intake.deviceQuantity != null ? ` · Qty: ${intake.deviceQuantity}` : ''}
            {intake.mounted ? ' · Mounted' : ''}
          </div>
          {intake.mismatch && (
            <div style={{ marginTop: 6, color: colors.danger }}>
              Mismatch vs. expected{intake.mismatchNotes ? `: ${intake.mismatchNotes}` : ''}
            </div>
          )}
          {(intake.receivedItems || []).length > 0 && (
            <div style={{ marginTop: 6 }}>
              {intake.receivedItems.map((item, i) => (
                <div key={i}>
                  • {item.name || 'Item'}{item.quantity ? ` ×${item.quantity}` : ''}
                  {(item.serials || []).length > 0 && (
                    <span style={panelStyles.meta}> ({item.serials.join(', ')})</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {(intake.defects || []).length > 0 && (
            <div style={{ marginTop: 6 }}>
              {intake.defects.map((defect, i) => (
                <div key={i} style={{ color: colors.statusYellow }}>
                  ⚠ {defect.type || 'defect'}{defect.notes ? ` — ${defect.notes}` : ''}
                </div>
              ))}
            </div>
          )}
          {intake.notes && <div style={{ marginTop: 6 }}>{intake.notes}</div>}
        </>
      )}
      {receipt?.confirmedAt && (
        <div style={{ marginTop: intake ? 8 : 0, ...panelStyles.meta }}>
          Receipt confirmed {fmtWhen(receipt.confirmedAt)}{receipt.confirmedBy ? ` by ${receipt.confirmedBy}` : ''}
          {receipt.mismatch?.hasMismatch && (
            <div style={{ color: colors.danger }}>
              Receipt mismatch{receipt.mismatch.summary ? `: ${receipt.mismatch.summary}` : ''}
              {(receipt.mismatch.fields || []).length > 0 ? ` (${receipt.mismatch.fields.join(', ')})` : ''}
            </div>
          )}
          {(receipt.missingItems || []).length > 0 && (
            <div style={{ marginTop: 4 }}>
              {receipt.missingItems.map((item, i) => (
                <div key={i} style={{ color: colors.danger }}>
                  ✗ Customer forgot: {item.name}{item.qty > 1 ? ` ×${item.qty}` : ''}{item.note ? ` — ${item.note}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ label, children }) {
  if (children == null || children === '') return null;
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
      <span style={{ ...panelStyles.meta, minWidth: 92, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.74rem' }}>{children}</span>
    </div>
  );
}

// Per-part confirmation list from the receipt check: what was expected vs what
// was actually confirmed received, item by item.
function ReceiptComparison({ receipt }) {
  const expected = receipt?.expected;
  const found = receipt?.found;
  if (!expected && !found) return null;
  const rows = [];
  const ok = (match, text) => (
    <span style={{ color: match ? colors.primary : colors.danger }}>
      {match ? '✓' : '✗'} {text}
    </span>
  );

  if (found?.mainDeviceSerial || expected?.mainDeviceSerial) {
    const match = !expected?.mainDeviceSerial || !found?.mainDeviceSerial
      || expected.mainDeviceSerial === found.mainDeviceSerial;
    rows.push(ok(match, `Main device SN ${found?.mainDeviceSerial || '—'}${!match ? ` (expected ${expected.mainDeviceSerial})` : ''}`));
  }
  if (found?.mountingOption || expected?.mountingOption) {
    const match = !expected?.mountingOption || expected.mountingOption === found?.mountingOption;
    rows.push(ok(match, `Mounting: ${found?.mountingOption || 'none'}${!match ? ` (expected ${expected.mountingOption})` : ''}`));
  }
  if (expected?.hmiIncluded != null || found?.hmiIncluded != null) {
    const match = expected?.hmiIncluded == null || expected.hmiIncluded === found?.hmiIncluded;
    rows.push(ok(match, `HMI ${found?.hmiIncluded ? 'included' : 'not included'}${!match ? ` (expected ${expected.hmiIncluded ? 'included' : 'not included'})` : ''}`));
  }
  const expectedAcc = new Map((expected?.accessories || []).map(a => [a.code, a]));
  const foundAcc = new Map((found?.accessories || []).map(a => [a.code, a]));
  const codes = [...new Set([...expectedAcc.keys(), ...foundAcc.keys()])].filter(Boolean);
  for (const code of codes) {
    const exp = expectedAcc.get(code);
    const got = foundAcc.get(code);
    const expQty = exp?.included ? (exp.qty || 1) : 0;
    const gotQty = got?.included ? (got.qty || 1) : 0;
    const match = expQty === gotQty;
    rows.push(ok(match, `${code} ×${gotQty}${!match ? ` (expected ×${expQty})` : ''}`));
  }
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 4 }}>
      {rows.map((row, i) => (
        <KV key={i} label={i === 0 ? 'Confirmed parts' : ''}>{row}</KV>
      ))}
    </div>
  );
}

// Deep, stage-specific context for an expanded timeline entry: the full data
// recorded for that part of the case, not just the transition note.
function StageDetail({ rma, status }) {
  const blocks = [];

  if (status === 'pending') {
    // Everything the customer entered in the start-RMA form.
    const addr = rma.shippingAddress || {};
    const returnAddress = [addr.recipientName, addr.streetAddress,
      [addr.postalCode, addr.city].filter(Boolean).join(' '), addr.region, addr.country]
      .filter(Boolean).join(', ');
    const ship = rma.customerShipment || {};
    const tracking = ship.trackingNumber || rma.customerTrackingNumber;
    const carrier = ship.carrier || rma.customerTrackingCarrier;
    const shippedAt = ship.shippedAt || rma.customerShipmentDate;
    blocks.push(
      <div key="sub">
        <KV label="Contact">{[rma.customerName, rma.contactOrg, rma.contactEmail].filter(Boolean).join(' · ')}</KV>
        <KV label="Serial">{rma.serialNumber}</KV>
        <KV label="Product no.">{rma.productNumber}</KV>
        <KV label="Issue type">{[rma.issueType, rma.returnReason].filter(Boolean).join(' · ')}</KV>
        <KV label="Priority">{rma.priority}</KV>
        <KV label="Reported issue">{rma.description}</KV>
        <KV label="Customer expects">{rma.customerExpectation}</KV>
        <KV label="Return address">{returnAddress}</KV>
        {(tracking || carrier || shippedAt) && (
          <KV label="Customer shipment">
            {[carrier, tracking, shippedAt ? `shipped ${fmtWhen(shippedAt)}` : '', ship.comment].filter(Boolean).join(' · ')}
          </KV>
        )}
        {rma.preScreened != null && (
          <KV label="Pre-screened">{`${rma.preScreened ? 'Yes' : 'No'}${rma.screeningNotes ? ` — ${rma.screeningNotes}` : ''}`}</KV>
        )}
        {(rma.submissionImages || []).length > 0 && (
          <KV label="Attachments">{`${rma.submissionImages.length} image(s) submitted`}</KV>
        )}
      </div>
    );
  }

  if (status === 'assigned') {
    blocks.push(
      <div key="assign">
        <KV label="Assigned location">{locationLabel(rma) || '—'}</KV>
        <KV label="Issue type">{rma.issueType}</KV>
        <KV label="Product no.">{rma.productNumber}</KV>
        <KV label="Reported issue">{rma.description}</KV>
      </div>
    );
  }

  if (status === 'received') {
    const intake = rma.receiptIntake;
    const receipt = rma.receipt;
    blocks.push(
      <div key="intake">
        {intake ? (
          <>
            <KV label="Intake result">
              <span style={panelStyles.chip(intake.status !== 'issue')}>
                {intake.status === 'issue' ? 'ISSUE' : 'OK'}
              </span>
            </KV>
            <KV label="Recorded">{`${fmtWhen(intake.recordedAt)}${intake.recordedBy ? ` by ${intake.recordedBy}` : ''}`}</KV>
            <KV label="Shipment">{`${intake.shipmentType || '—'} · qty ${intake.deviceQuantity ?? '—'}${intake.mounted ? ' · mounted' : ''}`}</KV>
            {intake.mount && <KV label="Mount">{`${intake.mount.type || ''}${intake.mount.hmi ? ' · HMI included' : ''}`}</KV>}
            {intake.accessories && (
              <KV label="Accessories">
                {`AC cable ×${intake.accessories.acPowerCableQty || 0} · HDMI ×${intake.accessories.hdmiCableQty || 0} · DP ×${intake.accessories.dpCableQty || 0} · Touch ×${intake.accessories.touchCableQty || 0}`}
              </KV>
            )}
            {(intake.receivedItems || []).map((item, i) => (
              <KV key={`item${i}`} label={i === 0 ? 'Items' : ''}>
                {`${item.name || 'Item'}${item.quantity ? ` ×${item.quantity}` : ''}${(item.serials || []).length ? ` (${item.serials.join(', ')})` : ''}`}
              </KV>
            ))}
            {(intake.defects || []).map((defect, i) => (
              <KV key={`def${i}`} label={i === 0 ? 'Defects' : ''}>
                <span style={{ color: colors.statusYellow }}>{`${defect.type || 'defect'}${defect.notes ? ` — ${defect.notes}` : ''}`}</span>
              </KV>
            ))}
            {intake.mismatch && (
              <KV label="Mismatch"><span style={{ color: colors.danger }}>{intake.mismatchNotes || 'Differs from expected shipment'}</span></KV>
            )}
            <KV label="Notes">{intake.notes}</KV>
          </>
        ) : (
          <div style={panelStyles.meta}>Intake control not recorded.</div>
        )}
        {receipt?.confirmedAt && (
          <>
            <KV label="Receipt">{`Confirmed ${fmtWhen(receipt.confirmedAt)}${receipt.confirmedBy ? ` by ${receipt.confirmedBy}` : ''}`}</KV>
            <KV label="Expected vs found">
              <span style={panelStyles.chip(!receipt.mismatch?.hasMismatch)}>
                {receipt.mismatch?.hasMismatch ? 'NOT MATCH' : 'MATCH'}
              </span>
            </KV>
            {receipt.mismatch?.hasMismatch && (
              <KV label="Deviation">
                <span style={{ color: colors.danger }}>
                  {receipt.mismatch.summary || 'Expected vs. found mismatch'}
                  {(receipt.mismatch.fields || []).length ? ` (${receipt.mismatch.fields.join(', ')})` : ''}
                </span>
              </KV>
            )}
            <ReceiptComparison receipt={receipt} />
            {(receipt.missingItems || []).map((item, i) => (
              <KV key={`miss${i}`} label={i === 0 ? 'Missing items' : ''}>
                <span style={{ color: colors.danger }}>
                  ✗ {item.name}{item.qty > 1 ? ` ×${item.qty}` : ''}{item.note ? ` — ${item.note}` : ''}
                </span>
              </KV>
            ))}
          </>
        )}
      </div>
    );
  }

  if (status === 'awaiting_repair' || status === 'repairing') {
    const repair = rma.repair || {};
    blocks.push(
      <div key="repair">
        <KV label="Phase">{repair.phase}</KV>
        {repair.startedAt && <KV label="Started">{`${fmtWhen(repair.startedAt)}${repair.startedBy ? ` by ${repair.startedBy}` : ''}`}</KV>}
        {repair.warranty?.decision && (
          <KV label="Warranty">{`${repair.warranty.decision}${repair.warranty.decidedBy ? ` (${repair.warranty.decidedBy}, ${fmtWhen(repair.warranty.decidedAt)})` : ''}${repair.warranty.note ? ` — ${repair.warranty.note}` : ''}`}</KV>
        )}
        {repair.inspectionFinalizedAt && (
          <KV label="Inspection">{`Finalized ${fmtWhen(repair.inspectionFinalizedAt)}${repair.inspectionFinalizedBy ? ` by ${repair.inspectionFinalizedBy}` : ''}`}</KV>
        )}
        {(repair.tasks || []).map((task, i) => (
          <KV key={`task${i}`} label={i === 0 ? 'Tasks' : ''}>
            <span style={{ color: task.done ? colors.primary : undefined }}>
              {`${task.done ? '✓' : '○'} ${task.label || 'Task'}${task.completedAt ? ` · ${fmtWhen(task.completedAt)}` : ''}${task.note ? ` — ${task.note}` : ''}`}
            </span>
          </KV>
        ))}
        {repair.summary && <KV label="Summary">{repair.summary}</KV>}
      </div>
    );
  }

  if (status === 'awaiting_shipping' || status === 'shipped') {
    const details = rma.shipping?.details || {};
    const dispatch = rma.shipping?.dispatch || {};
    const address = [details.addressLine1, details.addressLine2, [details.postalCode, details.city].filter(Boolean).join(' '), details.region, details.country]
      .filter(Boolean).join(', ');
    blocks.push(
      <div key="ship">
        <KV label="Recipient">{[details.recipientName, details.company].filter(Boolean).join(' · ')}</KV>
        <KV label="Contact">{[details.contactName, details.contactEmail, details.contactPhone].filter(Boolean).join(' · ')}</KV>
        <KV label="Address">{address}</KV>
        <KV label="Method">{details.method}</KV>
        <KV label="Payer">{rma.shipping?.payer}</KV>
        <KV label="Notes">{details.notes}</KV>
        {(dispatch.carrier || dispatch.trackingNumber || dispatch.shippedAt) && (
          <>
            <KV label="Carrier">{dispatch.carrier}</KV>
            <KV label="Tracking">{dispatch.trackingNumber}</KV>
            <KV label="Dispatched">{`${fmtWhen(dispatch.shippedAt)}${dispatch.dispatchedBy ? ` by ${dispatch.dispatchedBy}` : ''}`}</KV>
            <KV label="Dispatch notes">{dispatch.notes}</KV>
          </>
        )}
        {rma.shipping?.deliveryConfirmedAt && <KV label="Delivered">{fmtWhen(rma.shipping.deliveryConfirmedAt)}</KV>}
      </div>
    );
  }

  if (status === 'closed_case' || status === 'cancelled' || status === 'canceled_by_customer' || status === 'archived') {
    const cancel = rma.cancellation || {};
    if (cancel.at || cancel.reason || cancel.category) {
      blocks.push(
        <div key="cancel">
          <KV label="Closed / cancelled">{`${fmtWhen(cancel.at)}${cancel.by ? ` by ${cancel.by}` : ''}`}</KV>
          <KV label="Stage">{cancel.stage}</KV>
          <KV label="Category">{cancel.category}</KV>
          <KV label="Reason">{cancel.reason}</KV>
          <KV label="Notes">{cancel.notes}</KV>
        </div>
      );
    }
  }

  const imageStage = status === 'received' ? 'received'
    : (status === 'awaiting_repair' || status === 'repairing') ? 'repair'
      : (status === 'awaiting_shipping' || status === 'shipped') ? 'shipping' : null;
  const stageImages = imageStage ? (rma.caseImages || []).filter(img => img.stage === imageStage) : [];
  if (stageImages.length) {
    blocks.push(
      <div key="imgs">
        <KV label="Photos">{`${stageImages.length} attached`}</KV>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {stageImages.map((img, i) => (
            <a key={i} href={img.url} target="_blank" rel="noreferrer" title={img.caption || img.name}>
              <img
                src={img.url}
                alt={img.caption || img.name || 'case attachment'}
                style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: `1px solid ${colors.panelBorder}` }}
              />
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (!blocks.length) return null;
  return <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.panelBorder}` }}>{blocks}</div>;
}

// Audit-log actions and customer messages that happened while the case sat in
// this stage (between this transition and the next one).
function StageEvents({ rma, start, end }) {
  const startMs = start ? new Date(start).getTime() : null;
  const endMs = end ? new Date(end).getTime() : Infinity;
  if (startMs == null || Number.isNaN(startMs)) return null;
  const within = value => {
    const t = value ? new Date(value).getTime() : NaN;
    return !Number.isNaN(t) && t >= startMs && t < endMs;
  };
  const events = [
    ...(Array.isArray(rma.logs) ? rma.logs.filter(l => within(l.at)).map(l => ({
      at: l.at, text: `${l.action || 'action'}${l.by ? ` · ${l.by}` : ''}`
    })) : []),
    ...(Array.isArray(rma.customerNotes) ? rma.customerNotes.filter(n => within(n.createdAt)).map(n => ({
      at: n.createdAt, text: `Customer message${n.submittedBy ? ` (${n.submittedBy})` : ''}: ${(n.message || '').slice(0, 140)}${(n.message || '').length > 140 ? '…' : ''}`
    })) : [])
  ].sort((a, b) => new Date(a.at) - new Date(b.at));
  if (!events.length) return null;
  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.panelBorder}` }}>
      <div style={{ ...panelStyles.meta, marginBottom: 3 }}>During this stage</div>
      {events.map((event, i) => (
        <div key={i} style={{ ...panelStyles.meta, marginBottom: 2 }}>
          {fmtWhen(event.at)} · {event.text}
        </div>
      ))}
    </div>
  );
}

function TimelineBlock({ rma }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const entries = Array.isArray(rma.statusHistory) ? [...rma.statusHistory].reverse() : [];
  if (!entries.length) {
    return <div style={{ ...panelStyles.card, color: colors.muted }}>No status changes recorded.</div>;
  }
  const toggle = index => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(index)) next.delete(index); else next.add(index);
    return next;
  });
  return (
    <div>
      {entries.map((entry, i) => {
        const isOpen = expanded.has(i);
        return (
          <div
            key={i}
            onClick={() => toggle(i)}
            title={isOpen ? 'Collapse details' : 'Click for full details about this step'}
            style={{ ...panelStyles.card, borderLeft: `3px solid ${i === 0 ? colors.primary : colors.panelBorder}`, cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <strong>
                {entry.fromStatus ? `${statusLabel(entry.fromStatus)} → ` : ''}{statusLabel(entry.status)}
              </strong>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {entry.visibleToCustomer === false && (
                  <span style={{ ...panelStyles.meta, color: colors.statusYellow }}>internal</span>
                )}
                <span style={{ ...panelStyles.meta, color: colors.primary }}>{isOpen ? '▾' : '▸'}</span>
              </span>
            </div>
            <div style={panelStyles.meta}>
              {fmtWhen(entry.date)}{entry.user ? ` · ${entry.user}` : ''}
            </div>
            {entry.note && <div style={{ marginTop: 4 }}>{entry.note}</div>}
            {isOpen && (
              <div onClick={event => event.stopPropagation()} style={{ cursor: 'default' }}>
                {entry.visibleToCustomer != null && (
                  <KV label="Visibility">{entry.visibleToCustomer ? 'Visible to customer' : 'Internal only'}</KV>
                )}
                {(entry.fieldsChanged || []).length > 0 && (
                  <KV label="Fields changed">
                    <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: colors.muted }}>
                      {entry.fieldsChanged.join(', ')}
                    </span>
                  </KV>
                )}
                <StageDetail rma={rma} status={entry.status} />
                <StageEvents rma={rma} start={entry.date} end={i > 0 ? entries[i - 1].date : null} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RepairBlock({ rma }) {
  const repair = rma.repair;
  if (!repair || (!repair.startedAt && !(repair.tasks || []).length)) {
    return <div style={{ ...panelStyles.card, color: colors.muted }}>Repair not started yet.</div>;
  }
  const decision = repair.warranty?.decision;
  return (
    <div style={panelStyles.card}>
      <div style={panelStyles.meta}>
        Phase: {repair.phase || '—'}
        {repair.startedAt ? ` · Started ${fmtWhen(repair.startedAt)}${repair.startedBy ? ` by ${repair.startedBy}` : ''}` : ''}
      </div>
      {decision && (
        <div style={{ marginTop: 5 }}>
          Warranty decision: <strong>{decision}</strong>
          {repair.warranty?.decidedBy ? ` (${repair.warranty.decidedBy}, ${fmtWhen(repair.warranty.decidedAt)})` : ''}
          {repair.warranty?.note ? ` — ${repair.warranty.note}` : ''}
        </div>
      )}
      {(repair.tasks || []).length > 0 && (
        <div style={{ marginTop: 6 }}>
          {repair.tasks.map((task, i) => (
            <div key={i} style={{ color: task.done ? colors.primary : '#E6E9E7' }}>
              {task.done ? '✓' : '○'} {task.label || 'Task'}
              {task.completedAt && <span style={panelStyles.meta}> · {fmtWhen(task.completedAt)}</span>}
              {task.note && <span style={panelStyles.meta}> — {task.note}</span>}
            </div>
          ))}
        </div>
      )}
      {repair.summary && <div style={{ marginTop: 6 }}>{repair.summary}</div>}
      {repair.finalizedAt && (
        <div style={{ marginTop: 4, ...panelStyles.meta }}>
          Finalized {fmtWhen(repair.finalizedAt)}{repair.finalizedBy ? ` by ${repair.finalizedBy}` : ''}
        </div>
      )}
    </div>
  );
}

function CommunicationBlock({ rma }) {
  const notes = Array.isArray(rma.customerNotes) ? [...rma.customerNotes].reverse() : [];
  if (!notes.length && !rma.internalNotes) {
    return <div style={{ ...panelStyles.card, color: colors.muted }}>No messages recorded.</div>;
  }
  return (
    <div>
      {rma.internalNotes && (
        <div style={{ ...panelStyles.card, borderLeft: `3px solid ${colors.statusYellow}` }}>
          <div style={{ ...panelStyles.meta, color: colors.statusYellow }}>Internal notes</div>
          <div style={{ marginTop: 4 }}>{rma.internalNotes}</div>
        </div>
      )}
      {notes.map((note, i) => (
        <div key={i} style={panelStyles.card}>
          <div style={panelStyles.meta}>
            {fmtWhen(note.createdAt)}{note.submittedBy ? ` · ${note.submittedBy}` : ''}
            {(note.attachments || []).length > 0 ? ` · ${note.attachments.length} attachment(s)` : ''}
          </div>
          {note.message && <div style={{ marginTop: 4 }}>{note.message}</div>}
        </div>
      ))}
    </div>
  );
}

function ActivityBlock({ rma }) {
  const logs = Array.isArray(rma.logs) ? [...rma.logs].reverse().slice(0, 20) : [];
  if (!logs.length) return null;
  return (
    <Section title="Activity log">
      <div style={panelStyles.card}>
        {logs.map((log, i) => (
          <div key={i} style={{ ...panelStyles.meta, marginBottom: 3 }}>
            {fmtWhen(log.at)} · {log.action || 'action'}{log.by ? ` · ${log.by}` : ''}
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function RmaHistoryPanel({ rma, open, onToggle }) {
  if (!rma) return null;
  const panelWidth = 'min(420px, 92vw)';
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        title={open ? 'Hide case history' : 'Show the full case history for this RMA'}
        style={{ ...panelStyles.toggle, right: open ? panelWidth : 0 }}
      >
        {open ? 'Hide history ▸' : '◂ Case history'}
      </button>
      {open && (
        <div style={panelStyles.panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Case history</div>
            <span style={panelStyles.chip(true)}>{statusLabel(rma.status)}</span>
          </div>
          <div style={{ ...panelStyles.meta, marginTop: 4 }}>
            {rma.rmaId}{rma.serialNumber ? ` · SN ${rma.serialNumber}` : ''}
            {rma.submittedAt ? ` · Submitted ${fmtWhen(rma.submittedAt)}` : ''}
          </div>

          <Section title="Intake control"><IntakeBlock rma={rma} /></Section>
          <Section title="Status timeline"><TimelineBlock rma={rma} /></Section>
          <Section title="Repair"><RepairBlock rma={rma} /></Section>
          <Section title="Communication"><CommunicationBlock rma={rma} /></Section>
          <ActivityBlock rma={rma} />
        </div>
      )}
    </>
  );
}
